import type { ListRenderItem } from '@shopify/flash-list';
import { ResizeMode } from 'expo-av';
import { RefObject } from 'react';
import { ScrollViewProps, StyleProp, ViewStyle } from 'react-native';
import { CurrentShow } from '../../flows/showcase/functions/useCurrentShow/useCurrentShow';
import { Timestamp } from '../../functions/firebase/firebaseAccess';
import { WithID } from '../../functions/firebase/rxfireExtensions';
import { OpenListingScene } from '../../functions/hooks/useNavigateToProductItem';
import {
  ClipAuditLog,
  ClipFileType,
  ClipModerationInfo,
  Listing,
  MessageThankYouType,
  OrderVideoReviewEmoji,
  PopClipMetadata,
  PopClipStatus,
  Profile,
  VideoClipSourceType,
  VideoTranscodeStatus,
} from '../../models/ClientModels';

export enum AnyVideoType {
  LiveStream = 'liveStream',
  UpcomingShow = 'upcomingShow',
  LiveReplay = 'liveReplay',
  Review = 'review',
  ThankYou = 'thank-you',
  Clips = 'clips',
}

export type PopClipListing = WithID<Listing> & {
  csProductUrl?: string;
  shopifyProductInfo?: {
    shopDomain: string;
    productId: string;
    productHandle: string;
    productUrl: string;
  };
};

export type VideoInfoBase = {
  key: string;
  id: string;
  type: AnyVideoType;
  author: WithID<Profile> | undefined;

  // title of the video
  videoTitle?: string;
  // video description
  videoDescription?: string;
  // video url that is preferred as playing source
  videoURL?: string;
  // the original video url
  originalVideoURL?: string;
  // video cover image url used for display as thumbnail in
  // video list/grid view if gif or thumbnail is not available
  videoCoverUrl?: string;
  // video gif image url used for display in video list/grid view
  // if gif is not available, thumbnail or cover will be used
  gifURL?: string;
  // video thumbnail image url used for display as thumbnail
  // in video list/grid view
  thumbnailURL?: string;
  // the timestamp the video was created
  createdAt?: Timestamp;
  // the timestamp the video was updated.
  updatedAt?: Timestamp;

  // associated listing information
  listings?: WithID<Listing>[];
  variantId?: string;

  // optional properties that only support internal workings
  available?: boolean;
  visibility?: 'public' | 'private';

  /** extra information */
  extra?: Record<string, any>;
};

export type OrderVideoInfoBase = VideoInfoBase & {
  orderId?: string;
  transcodeStatus: VideoTranscodeStatus;
  from: WithID<Profile>;
  to: WithID<Profile>;
};

export type OrderReviewVideoInfo = OrderVideoInfoBase & {
  type: AnyVideoType.Review;
  messageId: string;
  emoji?: OrderVideoReviewEmoji;
  upvoteCount?: number;
};

export type OrderThankYouVideoInfo = OrderVideoInfoBase & {
  type: AnyVideoType.ThankYou;
  sellerId: string;
  messageId: string;
  thankYouType?: MessageThankYouType;
};

export type OrderVideoInfo = OrderReviewVideoInfo | OrderThankYouVideoInfo;

export type PopClipVideoInfo = Omit<VideoInfoBase, 'listings'> & {
  type: AnyVideoType.Clips;
  clipId: string;
  fileType: ClipFileType;
  cdnUrl: string;
  isExternal?: boolean;
  status?: PopClipStatus;
  listings?: PopClipListing[];
  customThumbnailMetaIdentifier?: string;
  importedFrom?: VideoClipSourceType;
  metaData: PopClipMetadata;
  moderation:
    | (ClipModerationInfo & {
        auditLogs: ClipAuditLog[];
      })
    | undefined;
  showLockedVideoThumbnail?: boolean;
};

export type LiveStreamVideoInfo = VideoInfoBase & {
  type: AnyVideoType.LiveStream;
  liveStreamId: string;
};

export type LiveReplayVideoInfo = VideoInfoBase & {
  type: AnyVideoType.LiveReplay;
  liveStreamId: string;
  masterPlaylist: string;
};

export type UpcomingShowVideoInfo = VideoInfoBase & {
  type: AnyVideoType.UpcomingShow;
  startsAt: Date;
  upcomingShowId: string;
};

export type AnyVideo =
  | PopClipVideoInfo
  | OrderReviewVideoInfo
  | OrderThankYouVideoInfo
  | LiveStreamVideoInfo
  | LiveReplayVideoInfo
  | UpcomingShowVideoInfo;

export type AnyVideoInfo = {
  readonly sellerId: string | undefined;
  readonly listingScene: OpenListingScene;
  readonly primaryProfile: WithID<Profile> | undefined;
  readonly relatedProfile: WithID<Profile> | undefined;
  readonly videoId: string;
  readonly playbackId: string;
  readonly listings: WithID<Listing>[];
  readonly currentShow: CurrentShow | null;
  readonly defaultListingImage: string | undefined;
  readonly defaultListing: WithID<Listing> | undefined;
};

export type AnyVideoClipsPagerViewProps<T extends AnyVideo = AnyVideo> = {
  initialScrollIndex?: number;
  estimatedItemSize: number;
  estimatedListSize: { width: number; height: number };
  listRef: RefObject<any>;
  keyExtractor: (item: T, index: number) => string;
  data: T[];
  renderItem: ListRenderItem<T>;
  extraData?: any;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  onEndReachedThreshold?: number;
  onEndReached?: () => void;
} & Omit<ScrollViewProps, 'contentContainerStyle'>;

export type AnyVideoContentCommonProps = {
  muted?: boolean;
  ready: boolean;
  shouldPlay: boolean;
  resizeMode?: ResizeMode;
  loop?: boolean;
  style?: StyleProp<ViewStyle>;
  active?: boolean;
  hidden?: boolean;
  onLoad?: () => void;
};

export type AnyVideoContentProps<T extends AnyVideo = AnyVideo> =
  AnyVideoContentCommonProps & {
    video: T;
  };
